/**
 * Mock SSN Card Verification Component
 *
 * A bare mock flow for testing SSN card verification SDK integration.
 * - Page 1: Start verification button
 * - Page 2: outcome picker (passed / failed / fraudulent / capture error)
 * - Page "confirming": shown for "Passed" only when the parent's IDME_INIT
 *   payload set confirmationScreenEnabled — mirrors the real document-service
 *   "passed" outcome landing on the confirmation screen instead of completing.
 *
 * Uses the postMessage protocol identical to the real SDK. Skips Regula entirely.
 * Payloads mirror the SSN card flow's contract in src/shared/types/ssnCardVerification.ts:
 *   - success: { data: SsnCardVerificationResponse } via VERIFICATION_COMPLETE
 *   - error:   SsnCardErrorPayload via VERIFICATION_ERROR
 *
 * Note: backend outcomes "failed" and "fraudulent" both collapse to client error
 * "verification_rejected" in the real hook — reproduced here, distinguished by message.
 */

import { useState, useEffect } from "react";
import { logger } from "../../shared/utils/logger";
import { resolveParentOrigin } from "../../shared/utils/parentOrigin";

type Page = "page1" | "page2" | "confirming";

const MOCK_UUID = "mock-ssn-card-uuid";

const MOCK_PASSED_PAYLOAD = {
  data: {
    uuid: MOCK_UUID,
    status: "completed",
    outcome: "passed",
    reason: { code: 200, name: "DOCUMENT_VERIFIED", description: "Mock SSN card verified" },
    results: {
      provider: "regula",
      resources: [
        { uuid: "mock-credential-uuid", href: "/resources/mock-credential-uuid", resourceType: "document_front_side" },
      ],
      attributes: {
        nameFirst: "Jane",
        nameLast: "Doe",
        dateOfBirth: "1990-01-15",
        documentNumber: "123-45-6789",
        documentType: "ssn",
      },
      providerData: { status: "success", verification: { id: "mock-regula-session-id" } },
    },
  },
};

export function SsnCardVerificationMock() {
  const [page, setPage] = useState<Page>("page1");
  const [parentOrigin, setParentOrigin] = useState<string | null>(null);
  const [confirmationScreenEnabled, setConfirmationScreenEnabled] = useState(false);
  const [outOfRetakes, setOutOfRetakes] = useState(false);

  useEffect(() => {
    try {
      // Resolve against the IDMe allowlist, same as the real SDK — never post a
      // verification result to an origin we don't recognise.
      const origin = resolveParentOrigin();
      if (!origin) {
        logger.log("[Mock SDK] Standalone mode - no allowlisted parent origin");
        return;
      }

      setParentOrigin(origin);

      window.parent.postMessage(
        { source: "idme-verification", type: "IDME_READY" },
        origin
      );

      logger.log("[Mock SDK] IDME_READY sent to parent, origin:", origin);

      const handleInitMessage = (event: MessageEvent) => {
        if (event.data?.type !== "IDME_INIT") {
          return;
        }
        if (event.origin !== origin) {
          logger.warn("[Mock SDK] Ignoring IDME_INIT from unexpected origin:", event.origin);
          return;
        }
        logger.log("[Mock SDK] Received IDME_INIT:", event.data.payload);
        setConfirmationScreenEnabled(event.data.payload?.confirmationScreenEnabled === true);
        setOutOfRetakes(event.data.payload?.outOfRetakes === true);
        window.removeEventListener("message", handleInitMessage);
      };

      window.addEventListener("message", handleInitMessage);

      return () => {
        window.removeEventListener("message", handleInitMessage);
      };
    } catch (error) {
      logger.error("[Mock SDK] Initialization error:", error);
    }
  }, []);

  const post = (message: Record<string, unknown>) => {
    if (parentOrigin) {
      window.parent.postMessage({ source: "idme-verification", ...message }, parentOrigin);
      setTimeout(() => {
        window.close();
      }, 100);
    }
  };

  const handlePassed = () => {
    if (confirmationScreenEnabled) {
      logger.log("[Mock SDK] Document service passed - showing confirmation screen");
      setPage("confirming");
      return;
    }
    logger.log("[Mock SDK] Sending VERIFICATION_COMPLETE (passed)");
    post({ type: "VERIFICATION_COMPLETE", payload: MOCK_PASSED_PAYLOAD });
  };

  const handleConfirm = () => {
    logger.log("[Mock SDK] Member confirmed - sending VERIFICATION_COMPLETE (passed)");
    post({ type: "VERIFICATION_COMPLETE", payload: MOCK_PASSED_PAYLOAD });
  };

  const handleRetake = () => {
    const errorCode = outOfRetakes ? "retake_limit_reached" : "user_retake_requested";
    logger.log(`[Mock SDK] Member requested retake from confirmation screen (errorCode: ${errorCode})`);
    post({
      type: "VERIFICATION_ERROR",
      payload: {
        errorCode,
        message: "User requested to retake photo of SSN card",
        data: { uuid: MOCK_UUID },
      },
    });
  };

  const handleRejected = (simulatedOutcome: "failed" | "fraudulent") => {
    logger.log(`[Mock SDK] Sending VERIFICATION_ERROR (verification_rejected, simulated: ${simulatedOutcome})`);
    post({
      type: "VERIFICATION_ERROR",
      payload: {
        errorCode: "verification_rejected",
        message: `SSN card verification was rejected (simulated outcome: ${simulatedOutcome})`,
        failedApi: "get_ssn_card_failed",
      },
    });
  };

  const handleCaptureError = () => {
    logger.log("[Mock SDK] Sending VERIFICATION_ERROR (camera_permission_denied)");
    post({
      type: "VERIFICATION_ERROR",
      payload: {
        errorCode: "camera_permission_denied",
        message: "Camera permission denied",
        feedback: "CAMERA_PERMISSION_DENIED",
      },
    });
  };

  const containerStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
    backgroundColor: "#f9f9f9",
    fontFamily: '"Open Sans", Arial, sans-serif',
    padding: "20px",
  };

  const contentStyle: React.CSSProperties = {
    textAlign: "center",
    maxWidth: "400px",
    backgroundColor: "white",
    padding: "40px",
    borderRadius: "8px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  };

  const headingStyle: React.CSSProperties = {
    fontSize: "24px",
    fontWeight: 600,
    color: "#2e3f51",
    margin: "0 0 12px 0",
  };

  const subtitleStyle: React.CSSProperties = {
    fontSize: "14px",
    color: "#666",
    margin: "0 0 32px 0",
  };

  const buttonStyle: React.CSSProperties = {
    padding: "12px 24px",
    fontSize: "16px",
    fontWeight: 600,
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    marginTop: "12px",
    width: "100%",
  };

  const primaryButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    backgroundColor: "#0050C7",
    color: "white",
  };

  const dangerButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    backgroundColor: "transparent",
    color: "#b00020",
    border: "1px solid #b00020",
  };

  const linkStyle: React.CSSProperties = {
    display: "inline-block",
    marginTop: "16px",
    color: "#0050C7",
    textDecoration: "underline",
    cursor: "pointer",
    fontSize: "14px",
  };

  const attributeRowStyle: React.CSSProperties = {
    textAlign: "left",
    fontSize: "14px",
    color: "#272727",
    padding: "8px 0",
    borderBottom: "1px solid #dfdfdf",
  };

  if (page === "confirming") {
    const { nameFirst, nameLast, documentNumber } = MOCK_PASSED_PAYLOAD.data.results.attributes;
    return (
      <div style={containerStyle}>
        <div style={contentStyle}>
          <h1 style={headingStyle}>Confirm your info</h1>
          <p style={subtitleStyle}>Mocked document service "passed" outcome</p>
          <div style={attributeRowStyle}>
            <strong>Name:</strong> {nameFirst} {nameLast}
          </div>
          <div style={attributeRowStyle}>
            <strong>SSN:</strong> {documentNumber}
          </div>
          <button style={primaryButtonStyle} onClick={handleConfirm}>
            Confirm
          </button>
          <span role="button" style={linkStyle} onClick={handleRetake}>
            {outOfRetakes ? "Join a video call" : "Retake photo"}
          </span>
        </div>
      </div>
    );
  }

  if (page === "page1") {
    return (
      <div style={containerStyle}>
        <div style={contentStyle}>
          <h1 style={headingStyle}>Mock SSN Card Verification</h1>
          <p style={subtitleStyle}>This is a test environment</p>
          <button style={primaryButtonStyle} onClick={() => setPage("page2")}>
            Start verification
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={contentStyle}>
        <h1 style={headingStyle}>Choose mock outcome</h1>
        <p style={subtitleStyle}>Each option emits the real SSN card payload shape</p>
        <button style={primaryButtonStyle} onClick={handlePassed}>
          Passed
        </button>
        <button style={dangerButtonStyle} onClick={() => handleRejected("failed")}>
          Failed (verification_rejected)
        </button>
        <button style={dangerButtonStyle} onClick={() => handleRejected("fraudulent")}>
          Fraudulent (verification_rejected)
        </button>
        <button style={dangerButtonStyle} onClick={handleCaptureError}>
          Capture error (camera_permission_denied)
        </button>
      </div>
    </div>
  );
}
